import { MutationFunction } from '@tanstack/react-query'
import api, { ApiResponse } from '.'
import { Mission } from './missions'
import { WorkPeriodEvent } from './work-period-events'
import { WorkPeriodSummary } from '../../../backend/src/services/resources/work-period-summaries/work-period-summary.types'
import {
  AbandonParams,
  CancelParams,
} from '../../../backend/src/services/resources/missions/mission-lifecycle.business.service.types'
import { ClockingRules } from './clocking-rules'
import { WorkPeriodClocking } from '../../../backend/src/services/resources/work-periods/work-period.model'
import { User } from './users'
import { Service } from './services'
import { WorkPeriodTag } from './work-period-tags'
import { subMilliseconds } from 'date-fns'

export interface WorkPeriodWithSummaryComment {
  message?: string
  user?: User
}

export interface WorkPeriodWithSummary extends WorkPeriod {
  summary: WorkPeriodSummary
}

interface WorkPeriodClockingTimeComment {
  createdAt: Date
  message: string
  user: User
}

interface WorkPeriodClockingTime {
  date: Date
  clocking?: WorkPeriodClocking
  manualClocking?: Date
  comments: WorkPeriodClockingTimeComment[]
}

export interface WorkPeriod {
  start: WorkPeriodClockingTime
  user: User
  end: WorkPeriodClockingTime
  manualBreak?: number
  breaksClockings: WorkPeriodClocking[]
  week: number
  year: number
  isValidated: boolean
  isAbandoned: boolean
  clockingRules: ClockingRules
  isCancelled: boolean
  service: Service
  periodSummary?: string
  _id: string
  mission: Mission
  tags: WorkPeriodTag[]
  events: {
    item: WorkPeriodEvent
    comment: string
  }[]
  __actions: {
    canBeValidated: boolean
    canBeInvalidated: boolean
    canBeUpdated: boolean
    canBeRemoved: boolean
    canBeAbandoned: boolean
    canBeCancelled: boolean
    canBeManualClocked: boolean
    canBeSummarized: boolean
    canBeReplaced: boolean
  }
}

export interface WorkPeriodCreationPayload {
  missionId: string
  start: { date: Date }
  end: { date: Date }
}

export interface WorkPeriodUpdatePayload {
  missionId: string
  workPeriodId: string
  start?: { date?: Date; manualClocking?: Date | null; comment?: string }
  end?: { date?: Date; manualClocking?: Date | null; comment?: string }
  manualBreak?: number | null
  isValidated?: boolean
  isAbandoned?: boolean
  isCancelled?: boolean
  isReplaceable?: boolean
  events?: { item: string; comment?: string }[]
  service?: string
}

export interface WorkPeriodAddEventsToWorkPeriodsPayload {
  missionId: string
  from: Date
  to: Date
  events: { item: string; comment?: string }[]
}

export interface WorkPeriodRemovePayload {
  missionId: string
  workPeriodId: string
}

export interface WorkPeriodWithMission extends WorkPeriod {
  mission: Mission
}

export interface WorkPeriodWithMissionAndSummary
  extends WorkPeriodWithMission,
    WorkPeriodWithSummary {}

/**
 * Fetch work periods from the API.
 * Transforms convenience params (start, end, events) into mongoose-query-parser
 * compatible field-level operators (start.date>=, start.date<=, events.item).
 */
/** Optional query params for GET /work-periods/:id */
export interface WorkPeriodGetParams {
  summary?: boolean
}

/**
 * Fetch a single work period by ID.
 * Route: GET /work-periods/:id
 *
 * The backend populates by default: mission, mission.jobTitle, mission.employer,
 * mission.interimAgency, mission.user, events, jobTitle, comments, service.
 *
 * @param idOrParams - The work period ID (string) or an object { id, summary? }
 *   when called via useResource with extra params.
 * @returns The work period with its populated mission.
 *   When summary=true, the response also includes the computed summary.
 */
export const get = async (
  idOrParams: string | ({ id: string } & WorkPeriodGetParams),
): Promise<ApiResponse<WorkPeriodWithMission>> => {
  const id = typeof idOrParams === 'string' ? idOrParams : idOrParams.id
  const summary = typeof idOrParams === 'string' ? undefined : idOrParams.summary

  return await api({
    endpoint: `/work-periods/${id}`,
    method: 'get',
    params: {
      ...(summary ? { summary: true } : {}),
    },
  })
}

export const fetch = async (data: any): Promise<ApiResponse<WorkPeriodWithMission>> => {
  const { start, end, events, 'events!': excludedEvents, ...rest } = data || {}

  return await api({
    endpoint: '/work-periods',
    method: 'get',
    params: {
      ...rest,
      // Map start/end to MongoDB date range on start.date.
      // Use end as-is (e.g. endOfWeek) so the upper bound is strictly before next day;
      // adding 1ms would send Monday 00:00:00 and can include the next week's Monday due to timezone/serialization.
      ...(start ? { 'start.date>': subMilliseconds(start, 1) } : {}),
      ...(end ? { 'start.date<': end } : {}),
      // Map events shorthand to the actual nested field (events.item)
      ...(events ? { 'events.item': events } : {}),
      // Map events negation to the nested field (events.item!)
      ...(excludedEvents ? { 'events.item!': excludedEvents } : {}),
    },
  })
}

export const create: MutationFunction<
  ApiResponse<Mission>,
  WorkPeriodCreationPayload
> = async data => {
  const { missionId, ...payload } = data
  return await api({
    endpoint: `/missions/${missionId}/work-periods`,
    method: 'post',
    params: payload,
  })
}

export const update: MutationFunction<
  ApiResponse<Mission>,
  WorkPeriodUpdatePayload
> = async data => {
  const { missionId, workPeriodId, ...payload } = data
  return await api({
    endpoint: `/missions/${missionId}/work-periods/${workPeriodId}`,
    method: 'put',
    params: payload,
  })
}

export type WorkPeriodValidatePayload = {
  workPeriodId: WorkPeriod['_id']
  isValidated?: boolean
}

export const validate: MutationFunction<
  ApiResponse<Mission>,
  WorkPeriodValidatePayload
> = async data => {
  const { workPeriodId, ...payload } = data
  return await api({
    endpoint: `/work-periods/${workPeriodId}/validate`,
    method: 'put',
    params: payload,
  })
}

export type WorkPeriodValidateManyPayload = {
  workPeriodsIds: WorkPeriod['_id'][]
  isValidated?: boolean
}

export const validateMany: MutationFunction<
  ApiResponse<Mission>,
  WorkPeriodValidateManyPayload
> = async data => {
  return await api({
    endpoint: `/work-periods/validate-many`,
    method: 'put',
    params: data,
  })
}

export type CancelMissionPayload = {
  missionId: Mission['_id']
  workPeriodId: WorkPeriod['_id']
} & Omit<CancelParams['payload'], 'updatedBy'>

export const cancel: MutationFunction<ApiResponse<Mission>, CancelMissionPayload> = async data => {
  const { missionId, workPeriodId, ...payload } = data
  return await api({
    endpoint: `/missions/${missionId}/work-periods/${workPeriodId}/cancel`,
    method: 'put',
    params: payload,
  })
}

export type AbandonMissionPayload = {
  missionId: Mission['_id']
  workPeriodId: WorkPeriod['_id']
} & Omit<AbandonParams['payload'], 'updatedBy'>

export const abandon: MutationFunction<
  ApiResponse<Mission>,
  AbandonMissionPayload
> = async data => {
  const { missionId, workPeriodId, ...payload } = data
  return await api({
    endpoint: `/missions/${missionId}/work-periods/${workPeriodId}/abandon`,
    method: 'put',
    params: payload,
  })
}

export const addEventsToWorkPeriods: MutationFunction<
  ApiResponse<Mission>,
  WorkPeriodAddEventsToWorkPeriodsPayload
> = async data => {
  const { missionId, ...payload } = data
  return await api({
    endpoint: `/missions/${missionId}/work-periods/add-events`,
    method: 'put',
    params: payload,
  })
}

export const remove: MutationFunction<
  ApiResponse<Mission>,
  WorkPeriodRemovePayload
> = async data => {
  const { missionId, workPeriodId } = data
  return await api({
    endpoint: `/missions/${missionId}/work-periods/${workPeriodId}`,
    method: 'delete',
  })
}

export type WorkPeriodUpdateTagsPayload = {
  workPeriodId: string
  tags?: string[]
}

export const updateTags: MutationFunction<
  ApiResponse<WorkPeriod>,
  WorkPeriodUpdateTagsPayload
> = async data => {
  const { workPeriodId, ...payload } = data
  return await api({
    endpoint: `/work-periods/${workPeriodId}/tags`,
    method: 'put',
    params: payload,
  })
}

export type WorkPeriodAddTagsToWorkPeriodsPayload = {
  missionId: string
  from: Date
  to: Date
  tags: string[]
}

export const addTagsToWorkPeriods: MutationFunction<
  ApiResponse<Mission>,
  WorkPeriodAddTagsToWorkPeriodsPayload
> = async data => {
  const { missionId, ...payload } = data
  return await api({
    endpoint: `/missions/${missionId}/work-periods/add-tags`,
    method: 'put',
    params: payload,
  })
}

/**
 * Payload for the materialize-day endpoint.
 * Materializes (creates) the work period for a specific day on a template-based mission.
 */
export type MaterializeSingleDayPayload = {
  /** The mission ID to materialize a day for. */
  missionId: Mission['_id']
  /** ISO date string – only the day part matters, time is ignored by the backend. */
  date: string
}

/**
 * Materialize (create) the work period for a single day on a template-based mission.
 *
 * POST /api/missions/:missionId/materialize-day
 *
 * - If the work period already exists for that day, it is returned as-is (idempotent).
 * - If the day has no scheduled slot in the template, the backend returns null.
 *
 * @param data - { missionId, date }
 * @returns The materialized work period or null.
 */
export const materializeSingleDay: MutationFunction<
  ApiResponse<WorkPeriodWithMission | null>,
  MaterializeSingleDayPayload
> = async data => {
  const { missionId, ...payload } = data
  return await api({
    endpoint: `/missions/${missionId}/materialize-day`,
    method: 'post',
    params: payload,
  })
}
